import {FC, useCallback, useEffect, useRef, useState} from 'react'
import {KTSVG} from '../../../../_metronic/helpers'
import {useAuth} from '../core/Auth'
import {endImpersonation} from '../core/_requests'
import {
  getImpersonatedAuth,
  getImpersonatorAuth,
  removeImpersonatedAuth,
  removeImpersonatorAuth,
  removeTabAuthOverride,
  setTabAuthOverride,
} from '../core/AuthHelpers'

const ImpersonationBanner: FC = () => {
  const {auth, currentUser} = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [automaticRetryAfterExpiry, setAutomaticRetryAfterExpiry] = useState(false)
  const closingRef = useRef(false)

  const returnToAdmin = useCallback(async (automatic = false) => {
    if (closingRef.current || !currentUser?.isImpersonating) return
    closingRef.current = true
    setLoading(true)
    setError('')

    const administratorAuth = getImpersonatorAuth()
    if (!administratorAuth?.api_token) {
      setError('Yönetici oturumu bu sekmede bulunamadı. Güvenli dönüş için yeniden giriş yapın.')
      setLoading(false)
      closingRef.current = false
      return
    }

    const storedTargetAuth = getImpersonatedAuth()
    if (
      !auth?.api_token ||
      !storedTargetAuth?.api_token ||
      storedTargetAuth.api_token !== auth.api_token
    ) {
      setError('Bu sekmedeki kullanıcı oturumu doğrulanamadı. Sayfayı yenileyip yeniden deneyin.')
      setLoading(false)
      closingRef.current = false
      return
    }

    // Stage the original signed admin token as a tab-only override before
    // revoking the target. The target remains first in token precedence until
    // the server confirms the close operation.
    if (!setTabAuthOverride(administratorAuth)) {
      setError('Yönetici oturumu bu sekmede güvenli olarak etkinleştirilemedi.')
      setLoading(false)
      closingRef.current = false
      return
    }

    try {
      await endImpersonation(storedTargetAuth.api_token)
    } catch (requestError: any) {
      console.error('Impersonation session could not be closed cleanly', requestError)
      const status = requestError?.response?.status

      if (status === 401) {
        // An expired/revoked target token cannot be used further. Resume only
        // with the original signed administrator token held by this tab.
        removeImpersonatedAuth()
        removeImpersonatorAuth()
        window.location.replace('/dashboard')
        return
      }

      // A network or server failure does not prove revocation/audit completed.
      // Keep both target token and administrator proof intact for a retry.
      removeTabAuthOverride()
      if (automatic) setAutomaticRetryAfterExpiry(true)
      const responseMessage = requestError?.response?.data?.message
      setError(
        String(
          responseMessage ||
            (automatic
              ? 'Oturum süresi dolmadan güvenli dönüş tamamlanamadı. Bağlantıyı kontrol edip yeniden deneyin.'
              : 'Yönetici hesabına dönüş tamamlanamadı. Bağlantıyı kontrol edip yeniden deneyin.')
        )
      )
      setLoading(false)
      closingRef.current = false
      return
    }

    removeImpersonatedAuth()
    removeImpersonatorAuth()
    window.location.replace('/dashboard')
  }, [auth?.api_token, currentUser?.isImpersonating])

  useEffect(() => {
    if (!currentUser?.isImpersonating || !currentUser.impersonationExpiresAt) return

    const expirationTime = Date.parse(currentUser.impersonationExpiresAt)
    if (!Number.isFinite(expirationTime)) return

    // Revoke and audit shortly before expiry. Clock skew or an already expired
    // target is handled by the safe 401 path above.
    const targetTime = automaticRetryAfterExpiry
      ? expirationTime + 1000
      : expirationTime - 5000
    const delay = Math.max(0, targetTime - Date.now())
    const timer = window.setTimeout(
      () => void returnToAdmin(true),
      Math.min(delay, 2_147_483_647)
    )

    return () => window.clearTimeout(timer)
  }, [
    automaticRetryAfterExpiry,
    currentUser?.impersonationExpiresAt,
    currentUser?.isImpersonating,
    returnToAdmin,
  ])

  if (!currentUser?.isImpersonating) return null

  const expiresAt = currentUser.impersonationExpiresAt
    ? new Date(currentUser.impersonationExpiresAt).toLocaleTimeString('tr-TR', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : null

  return (
    <div
      className='d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 px-5 px-lg-10 py-3 border-top border-bottom'
      style={{backgroundColor: '#fff3cd', borderColor: '#e6c65c', color: '#533f03'}}
      role='status'
      aria-live='polite'
    >
      <div className='d-flex align-items-start align-items-md-center gap-3 min-w-0'>
        <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-2x' />
        <div className='min-w-0'>
          <div className='fw-bolder fs-6'>
            {currentUser.fullName || currentUser.email} kullanıcısı olarak işlem yapıyorsunuz
          </div>
          <div className='fs-7 fw-semibold'>
            Yönetici: {currentUser.impersonatorName || `#${currentUser.impersonatorId}`}
            {expiresAt ? ` · Oturum ${expiresAt} saatine kadar geçerli` : ''}
          </div>
          {error && <div className='text-danger fw-bolder fs-7 mt-1'>{error}</div>}
        </div>
      </div>
      <button
        type='button'
        className='btn btn-sm btn-dark flex-shrink-0'
        onClick={() => void returnToAdmin(false)}
        disabled={loading}
      >
        {loading ? (
          <span className='spinner-border spinner-border-sm me-2' aria-hidden='true' />
        ) : (
          <KTSVG path='/media/icons/duotune/arrows/arr076.svg' className='svg-icon-3 me-1' />
        )}
        Yönetici hesabına dön
      </button>
    </div>
  )
}

export default ImpersonationBanner
